// src/store/builders/useERPStore.js
import { create } from "zustand";
import { toast } from "react-hot-toast";
import { axiosInstancev1 } from "../../lib/axiosInstance";
import useAuthStore from "../useAuthStore";

const getBuilder = () => {
  const { user } = useAuthStore.getState();
  return user?.id;
};

const api = (method, url, data) =>
  axiosInstancev1[method](url, data).then((r) => r.data);

const useERPStore = create((set, get) => ({
  // ─── State ────────────────────────────────────────────────────────────────
  vendors: [],
  vendorPayments: [],
  vendorOutstandingBalances: [],
  purchases: [],
  employees: [],
  contractors: [],
  assignments: [],
  attendance: [],
  attendanceSummary: [],
  inventory: [],
  materialUsage: [],
  projectInventoryStatus: [],
  materialTransfers: [],
  pettyCash: [],
  pettyCashRequests: [],
  pettyCashExpenses: [],
  pettyCashAllocations: [],
  financeSummary: null,
  isLoading: false,
  error: null,

  // ─── Vendors ─────────────────────────────────────────────────────────────
  fetchVendors: async () => {
    const builderId = getBuilder();
    if (!builderId) return;
    set({ isLoading: true });
    try {
      const res = await api("get", `/erp/vendors?builderId=${builderId}`);
      set({ vendors: res.data || [], isLoading: false });
    } catch (e) {
      set({ isLoading: false });
      toast.error("Failed to load vendors");
    }
  },

  createVendor: async (data) => {
    const builderId = getBuilder();
    try {
      await api("post", "/erp/vendors", { ...data, builder_id: builderId });
      toast.success("Vendor created");
      await get().fetchVendors();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to create vendor");
      return false;
    }
  },

  updateVendor: async (id, data) => {
    const builderId = getBuilder();
    try {
      await api("put", `/erp/vendors/${id}`, { ...data, builder_id: builderId });
      toast.success("Vendor updated");
      await get().fetchVendors();
      return true;
    } catch (e) {
      toast.error("Failed to update vendor");
      return false;
    }
  },

  deleteVendor: async (id) => {
    const builderId = getBuilder();
    try {
      await api("delete", `/erp/vendors/${id}?builderId=${builderId}`);
      toast.success("Vendor deleted");
      await get().fetchVendors();
    } catch (e) {
      toast.error("Failed to delete vendor");
    }
  },

  // ─── Purchases ───────────────────────────────────────────────────────────
  fetchAllPurchases: async () => {
    const builderId = getBuilder();
    if (!builderId) return;
    set({ isLoading: true });
    try {
      const res = await api("get", `/erp/purchases?builderId=${builderId}`);
      set({ purchases: res.data || [], isLoading: false });
    } catch (e) {
      set({ isLoading: false });
    }
  },

  fetchVendorPurchases: async (vendorId) => {
    const builderId = getBuilder();
    if (!builderId) return;
    try {
      const res = await api("get", `/erp/vendors/${vendorId}/purchases?builderId=${builderId}`);
      set({ purchases: res.data || [] });
    } catch (e) {
      toast.error("Failed to load purchases");
    }
  },

  createPurchase: async (data) => {
    const builderId = getBuilder();
    try {
      await api("post", "/erp/purchases", { ...data, builder_id: builderId });
      toast.success("Purchase recorded & inventory updated");
      await get().fetchAllPurchases();
      await get().fetchInventory();
      return true;
    } catch (e) {
      toast.error("Failed to record purchase");
      return false;
    }
  },

  updatePurchase: async (id, data) => {
    const builderId = getBuilder();
    try {
      await api("put", `/erp/purchases/${id}`, { ...data, builder_id: builderId });
      toast.success("Purchase updated");
      await get().fetchAllPurchases();
      return true;
    } catch (e) {
      toast.error("Failed to update purchase");
      return false;
    }
  },

  deletePurchase: async (id) => {
    const builderId = getBuilder();
    try {
      await api("delete", `/erp/purchases/${id}?builderId=${builderId}`);
      toast.success("Purchase deleted");
      await get().fetchAllPurchases();
    } catch (e) {
      toast.error("Failed to delete purchase");
    }
  },

  fetchVendorPayments: async (vendorId) => {
    const builderId = getBuilder();
    if (!builderId) return;
    try {
      const res = await api("get", `/erp/vendors/${vendorId}/payments?builderId=${builderId}`);
      set({ vendorPayments: res.data || [] });
    } catch (e) {
      toast.error("Failed to load vendor payments");
    }
  },

  createVendorPayment: async (vendorId, data) => {
    const builderId = getBuilder();
    try {
      await api("post", `/erp/vendors/${vendorId}/payments`, { ...data, builder_id: builderId });
      toast.success("Payment recorded successfully");
      await get().fetchVendorPayments(vendorId);
      await get().fetchVendorOutstandingBalances();
      return true;
    } catch (e) {
      toast.error("Failed to record payment");
      return false;
    }
  },

  fetchVendorOutstandingBalances: async () => {
    const builderId = getBuilder();
    if (!builderId) return;
    try {
      const res = await api("get", `/erp/vendors/outstanding-balance?builderId=${builderId}`);
      set({ vendorOutstandingBalances: res.data || [] });
    } catch (e) {
      // quiet fail
    }
  },

  // ─── Employees ───────────────────────────────────────────────────────────
  fetchEmployees: async () => {
    const builderId = getBuilder();
    if (!builderId) return;
    set({ isLoading: true });
    try {
      const res = await api("get", `/erp/employees?builderId=${builderId}`);
      set({ employees: res.data || [], isLoading: false });
    } catch (e) {
      set({ isLoading: false });
      toast.error("Failed to load employees");
    }
  },

  createEmployee: async (data) => {
    const builderId = getBuilder();
    try {
      await api("post", "/erp/employees", { ...data, builder_id: builderId });
      toast.success("Employee added");
      await get().fetchEmployees();
      return true;
    } catch (e) {
      toast.error("Failed to add employee");
      return false;
    }
  },

  updateEmployee: async (id, data) => {
    const builderId = getBuilder();
    try {
      await api("put", `/erp/employees/${id}`, { ...data, builder_id: builderId });
      toast.success("Employee updated");
      await get().fetchEmployees();
      return true;
    } catch (e) {
      toast.error("Failed to update employee");
      return false;
    }
  },

  deleteEmployee: async (id) => {
    const builderId = getBuilder();
    try {
      await api("delete", `/erp/employees/${id}?builderId=${builderId}`);
      toast.success("Employee removed");
      await get().fetchEmployees();
    } catch (e) {
      toast.error("Failed to delete employee");
    }
  },

  // ─── Contractors ─────────────────────────────────────────────────────────
  fetchContractors: async () => {
    const builderId = getBuilder();
    if (!builderId) return;
    set({ isLoading: true });
    try {
      const res = await api("get", `/erp/contractors?builderId=${builderId}`);
      set({ contractors: res.data || [], isLoading: false });
    } catch (e) {
      set({ isLoading: false });
      toast.error("Failed to load contractors");
    }
  },

  createContractor: async (data) => {
    const builderId = getBuilder();
    try {
      await api("post", "/erp/contractors", { ...data, builder_id: builderId });
      toast.success("Contractor added");
      await get().fetchContractors();
      return true;
    } catch (e) {
      toast.error("Failed to add contractor");
      return false;
    }
  },

  updateContractor: async (id, data) => {
    const builderId = getBuilder();
    try {
      await api("put", `/erp/contractors/${id}`, { ...data, builder_id: builderId });
      toast.success("Contractor updated");
      await get().fetchContractors();
      return true;
    } catch (e) {
      toast.error("Failed to update contractor");
      return false;
    }
  },

  deleteContractor: async (id) => {
    const builderId = getBuilder();
    try {
      await api("delete", `/erp/contractors/${id}?builderId=${builderId}`);
      toast.success("Contractor removed");
      await get().fetchContractors();
    } catch (e) {
      toast.error("Failed to delete contractor");
    }
  },

  // ─── Assignments ─────────────────────────────────────────────────────────
  fetchAssignmentsByProject: async (projectId) => {
    const builderId = getBuilder();
    if (!builderId) return;
    try {
      const res = await api("get", `/erp/assignments/project/${projectId}?builderId=${builderId}`);
      set({ assignments: res.data || [] });
      return res.data || [];
    } catch (e) {
      toast.error("Failed to load assignments");
      return [];
    }
  },

  fetchAssignmentsByEmployee: async (employeeId) => {
    const builderId = getBuilder();
    if (!builderId) return [];
    try {
      const res = await api("get", `/erp/assignments/employee/${employeeId}?builderId=${builderId}`);
      return res.data || [];
    } catch (e) {
      toast.error("Failed to load assignments");
      return [];
    }
  },

  createAssignment: async (data) => {
    const builderId = getBuilder();
    try {
      await api("post", "/erp/assignments", { ...data, builder_id: builderId });
      toast.success("Employee assigned to project");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to assign employee");
      return false;
    }
  },

  updateAssignment: async (id, data) => {
    const builderId = getBuilder();
    try {
      await api("put", `/erp/assignments/${id}`, { ...data, builder_id: builderId });
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update assignment");
      return false;
    }
  },

  deleteAssignment: async (id) => {
    const builderId = getBuilder();
    try {
      await api("delete", `/erp/assignments/${id}?builderId=${builderId}`);
      toast.success("Assignment removed");
      return true;
    } catch (e) {
      toast.error("Failed to remove assignment");
      return false;
    }
  },

  // ─── Contractor Assignments ───────────────────────────────────────────────
  fetchContractorAssignments: async (contractorId) => {
    const builderId = getBuilder();
    if (!builderId || !contractorId) return [];
    try {
      const res = await api("get", `/erp/contractor-assignments/contractor/${contractorId}?builderId=${builderId}`);
      return res.data || [];
    } catch (e) {
      toast.error("Failed to load contractor assignments");
      return [];
    }
  },


  createContractorAssignment: async (data) => {
    const builderId = getBuilder();
    try {
      await api("post", "/erp/contractor-assignments", { ...data, builder_id: builderId });
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to assign contractor");
      return false;
    }
  },

  deleteContractorAssignment: async (id) => {
    const builderId = getBuilder();
    try {
      await api("delete", `/erp/contractor-assignments/${id}?builderId=${builderId}`);
      toast.success("Contractor assignment removed");
      return true;
    } catch (e) {
      toast.error("Failed to remove contractor assignment");
      return false;
    }
  },

  updateContractorAssignmentStatus: async (id, status, completedDate) => {
    const builderId = getBuilder();
    try {
      await api("put", `/erp/contractor-assignments/${id}/status`, {
        builder_id: builderId, status, completed_date: completedDate || null,
      });
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to update contract status");
      return false;
    }
  },

  fetchContractorRevertRequests: async (projectId) => {
    const builderId = getBuilder();
    if (!builderId) return [];
    try {
      let qs = `?builderId=${builderId}`;
      if (projectId) qs += `&projectId=${projectId}`;
      const res = await api("get", `/erp/contractor-assignments/revert-requests${qs}`);
      return res.data || [];
    } catch (e) {
      return [];
    }
  },

  approveContractorRevert: async (id) => {
    const builderId = getBuilder();
    try {
      await api("put", `/erp/contractor-assignments/${id}/approve-revert`, { builder_id: builderId });
      toast.success("Revert approved — contractor is active again");
      return true;
    } catch (e) {
      toast.error("Failed to approve revert");
      return false;
    }
  },

  rejectContractorRevert: async (id) => {
    const builderId = getBuilder();
    try {
      await api("put", `/erp/contractor-assignments/${id}/reject-revert`, { builder_id: builderId });
      toast.success("Revert rejected — contract remains completed");
      return true;
    } catch (e) {
      toast.error("Failed to reject revert");
      return false;
    }
  },

  fetchEmployeeAttendanceFull: async (employeeId, month) => {
    const builderId = getBuilder();
    if (!builderId) return { records: [], totalEarned: 0, totalPaid: 0, balance: 0 };
    try {
      let qs = `?builderId=${builderId}`;
      if (month) qs += `&month=${month}`;
      const res = await api("get", `/erp/employees/${employeeId}/attendance-full${qs}`);
      set({ 
        employeeAttendance: res.data.records || [], 
        attendanceSummary: {
          totalEarned: res.data.totalEarned || 0,
          totalPaid: res.data.totalPaid || 0,
          balance: res.data.balance || 0
        },
        loading: false 
      });
      return res.data;
    } catch (e) {
      return { records: [], totalEarned: 0, totalPaid: 0, balance: 0 };
    }
  },

  markAttendancePaid: async (attendanceId, paidAmount) => {
    try {
      await api("patch", `/erp/attendance/${attendanceId}/pay`, { paid_amount: parseFloat(paidAmount) });
      toast.success("Marked as paid");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to mark as paid");
      return false;
    }
  },

  payContractorAmount: async (assignmentId, paidAmount) => {
    try {
      await api("patch", `/erp/contractor-assignments/${assignmentId}/pay`, { paid_amount: parseFloat(paidAmount) });
      toast.success("Payment recorded");
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to record payment");
      return false;
    }
  },

  fetchSalaryHistory: async (employeeId) => {
    const builderId = getBuilder();
    if (!builderId) return [];
    try {
      const res = await api("get", `/erp/employees/${employeeId}/salary-history?builderId=${builderId}`);
      return res.data || [];
    } catch (e) {
      return [];
    }
  },

  // ─── Attendance ──────────────────────────────────────────────────────────
  fetchAttendance: async (projectId, dateFrom, dateTo) => {
    const builderId = getBuilder();
    if (!builderId) return [];
    try {
      const params = new URLSearchParams({ builderId });
      if (dateFrom) params.append("dateFrom", dateFrom);
      if (dateTo) params.append("dateTo", dateTo);
      const res = await api("get", `/erp/attendance/project/${projectId}?${params}`);
      set({ attendance: res.data || [] });
      return res.data || [];
    } catch (e) {
      toast.error("Failed to load attendance");
      return [];
    }
  },

  upsertAttendance: async (data) => {
    const builderId = getBuilder();
    try {
      await api("post", "/erp/attendance", { ...data, builder_id: builderId });
      return true;
    } catch (e) {
      toast.error("Failed to record attendance");
      return false;
    }
  },

  fetchAttendanceSummary: async (projectId) => {
    const builderId = getBuilder();
    if (!builderId) return;
    try {
      const res = await api("get", `/erp/attendance/summary/${projectId}?builderId=${builderId}`);
      set({ attendanceSummary: res.data || [] });
      return res.data || [];
    } catch (e) {
      toast.error("Failed to load attendance summary");
      return [];
    }
  },

  fetchEmployeeAttendance: async (employeeId, month) => {
    const builderId = getBuilder();
    if (!builderId) return [];
    try {
      let qs = `?builderId=${builderId}`;
      if (month) qs += `&month=${month}`;
      const res = await api("get", `/erp/attendance/employee/${employeeId}${qs}`);
      return res.data || [];
    } catch (e) {
      toast.error("Failed to load employee attendance");
      return [];
    }
  },

  // ─── Inventory ───────────────────────────────────────────────────────────
  fetchInventory: async () => {
    const builderId = getBuilder();
    if (!builderId) return;
    set({ isLoading: true });
    try {
      const res = await api("get", `/erp/inventory?builderId=${builderId}`);
      set({ inventory: res.data || [], isLoading: false });
    } catch (e) {
      set({ isLoading: false });
    }
  },

  createInventoryItem: async (data) => {
    const builderId = getBuilder();
    try {
      await api("post", "/erp/inventory", { ...data, builder_id: builderId });
      toast.success("Stock added to Godown");
      await get().fetchInventory();
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to add stock to Godown");
      return false;
    }
  },

  updateInventoryItem: async (id, data) => {
    const builderId = getBuilder();
    try {
      await api("put", `/erp/inventory/${id}`, { ...data, builder_id: builderId });
      toast.success("Inventory item updated");
      await get().fetchInventory();
      return true;
    } catch (e) {
      toast.error("Failed to update inventory item");
      return false;
    }
  },

  deleteInventoryItem: async (id) => {
    const builderId = getBuilder();
    try {
      await api("delete", `/erp/inventory/${id}?builderId=${builderId}`);
      toast.success("Item removed");
      await get().fetchInventory();
    } catch (e) {
      toast.error("Failed to remove item");
    }
  },

  // ─── Material Usage ──────────────────────────────────────────────────────
  fetchMaterialUsage: async (projectId) => {
    const builderId = getBuilder();
    if (!builderId) return;
    try {
      const res = await api("get", `/erp/material-usage/project/${projectId}?builderId=${builderId}`);
      set({ materialUsage: res.data || [] });
    } catch (e) {
      toast.error("Failed to load material usage");
    }
  },

  createMaterialUsage: async (data) => {
    const builderId = getBuilder();
    try {
      await api("post", "/erp/material-usage", { ...data, builder_id: builderId });
      toast.success("Usage recorded");
      return true;
    } catch (e) {
      toast.error("Failed to record usage");
      return false;
    }
  },

  deleteMaterialUsage: async (id) => {
    const builderId = getBuilder();
    try {
      await api("delete", `/erp/material-usage/${id}?builderId=${builderId}`);
      toast.success("Usage record deleted");
      return true;
    } catch (e) {
      toast.error("Failed to delete usage");
      return false;
    }
  },

  fetchProjectInventoryStatus: async (projectId) => {
    const builderId = getBuilder();
    if (!builderId) return;
    try {
      const res = await api("get", `/erp/inventory/project-status/${projectId}?builderId=${builderId}`);
      set({ projectInventoryStatus: res.data || [] });
    } catch (e) {
      toast.error("Failed to load inventory status");
    }
  },

  // ─── Material Transfers ──────────────────────────────────────────────────
  fetchMaterialTransfers: async () => {
    const builderId = getBuilder();
    if (!builderId) return;
    try {
      const res = await api("get", `/erp/material-transfers?builderId=${builderId}`);
      set({ materialTransfers: res.data || [] });
    } catch (e) {
      toast.error("Failed to load transfers");
    }
  },

  createMaterialTransfer: async (data) => {
    const builderId = getBuilder();
    const fromId = data.from_project_id;
    const isGodown = fromId === null || fromId === undefined || fromId === "" || fromId === 0 || fromId === "0";
    try {
      await api("post", "/erp/material-transfers", {
        ...data,
        builder_id: builderId,
        from_project_id: isGodown ? null : fromId,
      });
      toast.success(isGodown ? "Stock transferred from Godown" : "Transfer request submitted — awaiting Accept/Reject");
      await Promise.all([get().fetchMaterialTransfers(), get().fetchInventory()]);
      return true;
    } catch (e) {
      toast.error(e.response?.data?.message || "Failed to create transfer");
      return false;
    }
  },

  updateMaterialTransferStatus: async (id, status) => {
    const builderId = getBuilder();
    try {
      await api("put", `/erp/material-transfers/${id}/status`, { status, builder_id: builderId });
      toast.success(`Transfer ${status}`);
      await get().fetchMaterialTransfers();
      return true;
    } catch (e) {
      toast.error("Failed to update transfer");
      return false;
    }
  },

  // ─── Petty Cash ──────────────────────────────────────────────────────────
  fetchPettyCash: async () => {
    const builderId = getBuilder();
    if (!builderId) return;
    set({ isLoading: true });
    try {
      const res = await api("get", `/erp/petty-cash?builderId=${builderId}`);
      set({ pettyCash: res.data || [], isLoading: false });
    } catch (e) {
      set({ isLoading: false });
    }
  },

  upsertPettyCash: async (data) => {
    const builderId = getBuilder();
    try {
      await api("post", "/erp/petty-cash", { ...data, builder_id: builderId });
      toast.success("Petty cash allocated");
      await get().fetchPettyCash();
      return true;
    } catch (e) {
      toast.error(e?.response?.data?.message || "Failed to allocate petty cash");
      return false;
    }
  },

  fetchPettyCashAllocations: async (projectId) => {
    const builderId = getBuilder();
    if (!builderId || !projectId) return;
    try {
      const res = await api("get", `/erp/petty-cash/allocations/project/${projectId}?builderId=${builderId}`);
      set({ pettyCashAllocations: res.data || [] });
    } catch (e) {
      toast.error("Failed to load allocation history");
      set({ pettyCashAllocations: [] });
    }
  },

  fetchPettyCashRequests: async (status) => {
    const builderId = getBuilder();
    if (!builderId) return;
    try {
      const params = new URLSearchParams({ builderId });
      if (status) params.append("status", status);
      const res = await api("get", `/erp/petty-cash/requests?${params}`);
      set({ pettyCashRequests: res.data || [] });
    } catch (e) {
      toast.error("Failed to load requests");
    }
  },

  createPettyCashRequest: async (data) => {
    const builderId = getBuilder();
    try {
      await api("post", "/erp/petty-cash/requests", { ...data, builder_id: builderId });
      toast.success("Request submitted");
      await get().fetchPettyCashRequests();
      return true;
    } catch (e) {
      toast.error("Failed to submit request");
      return false;
    }
  },

  updatePettyCashRequestStatus: async (id, status, projectId, amount, notes) => {
    const builderId = getBuilder();
    try {
      await api("put", `/erp/petty-cash/requests/${id}/status`, { status, builder_id: builderId, project_id: projectId, amount, notes });
      toast.success(`Request ${status}`);
      await get().fetchPettyCashRequests();
      await get().fetchPettyCash();
      return true;
    } catch (e) {
      toast.error("Failed to update request");
      return false;
    }
  },

  fetchPettyCashExpenses: async (projectId) => {
    const builderId = getBuilder();
    if (!builderId) return;
    try {
      const res = await api("get", `/erp/petty-cash/expenses/project/${projectId}?builderId=${builderId}`);
      set({ pettyCashExpenses: res.data || [] });
    } catch (e) {
      toast.error("Failed to load expenses");
    }
  },

  createPettyCashExpense: async (data) => {
    const builderId = getBuilder();
    try {
      await api("post", "/erp/petty-cash/expenses", { ...data, builder_id: builderId });
      toast.success("Expense logged");
      return true;
    } catch (e) {
      toast.error("Failed to log expense");
      return false;
    }
  },

  deletePettyCashExpense: async (id) => {
    const builderId = getBuilder();
    try {
      await api("delete", `/erp/petty-cash/expenses/${id}?builderId=${builderId}`);
      toast.success("Expense deleted");
      return true;
    } catch (e) {
      toast.error("Failed to delete expense");
      return false;
    }
  },

  // ─── Finance ─────────────────────────────────────────────────────────────
  fetchFinanceSummary: async (filters = {}) => {
    const builderId = getBuilder();
    if (!builderId) return;
    set({ isLoading: true });
    try {
      let qs = `?builderId=${builderId}`;
      if (filters.dateFrom) qs += `&dateFrom=${filters.dateFrom}`;
      if (filters.dateTo) qs += `&dateTo=${filters.dateTo}`;
      const res = await api("get", `/erp/finance/summary${qs}`);
      set({ financeSummary: res.data, isLoading: false });
    } catch (e) {
      set({ isLoading: false });
      toast.error("Failed to load finance summary");
    }
  },
}));

export default useERPStore;
